/**
 * The company's Geosoft templates in Teslyn Cloud (MB's decision 889; docs/specs/cloud/geosoft-templates.md §3, §4).
 * An upload stores each file of a type's set in the private bucket `geosoft-templates`, named by its SHA-256 (so a file
 * already there is not sent again and nothing is ever overwritten), then `geosoft_template_replace` replaces the type's
 * manifest in one step; files no set uses any more are deleted afterwards. The database decides who may (tenant
 * admins, managers and data processors) and checks every file against its stored object.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  GEOSOFT_TEMPLATE_BUCKET,
  geosoftTemplateObjectPath,
  type GeosoftTemplateFile,
  type QcFamily,
} from '@teslyn/domain';
import { sha256Hex } from '@teslyn/sync';

export const GEOSOFT_TEMPLATE_COLUMNS =
  'id, tenant_id, family, rel_path, size_bytes, sha256, object_path, uploaded_by_name, created_at, updated_at, updated_by, deleted_at';

const REFUSED = 'Only a tenant admin, a manager or a data processor may change the Geosoft templates.';

function words(e: { readonly message: string; readonly code?: string }): string {
  if (e.code === '42501' || /row-level security|permission denied/i.test(e.message)) {
    return /Only a tenant admin/.test(e.message) ? e.message : REFUSED;
  }
  return e.message;
}

/** The organisation's live template files, by type and path. */
export async function loadGeosoftTemplates(
  sb: SupabaseClient,
  tenantId: string,
): Promise<GeosoftTemplateFile[]> {
  const r = await sb
    .from('geosoft_template_file')
    .select(GEOSOFT_TEMPLATE_COLUMNS)
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .order('family')
    .order('rel_path');
  if (r.error) throw new Error(`The Geosoft templates could not be read: ${r.error.message}`);
  return (r.data ?? []) as GeosoftTemplateFile[];
}

/** One file to upload: its path in the set and its bytes. */
export interface TemplateUploadFile {
  readonly rel_path: string;
  readonly bytes: Uint8Array;
}

/** A file with its hash, ready for `uploadGeosoftTemplate`. */
export interface HashedTemplateFile extends TemplateUploadFile {
  readonly sha256: string;
}

export async function hashTemplateFiles(files: readonly TemplateUploadFile[]): Promise<HashedTemplateFile[]> {
  return Promise.all(files.map(async (f) => ({ ...f, sha256: await sha256Hex(f.bytes) })));
}

const alreadyThere = (e: {
  readonly message: string;
  readonly statusCode?: string | number | undefined;
}): boolean => String(e.statusCode ?? '') === '409' || /already exists|duplicate/i.test(e.message);

/** Delete the stored files of `before` that no live file of the organisation names any more (best effort). */
async function deleteUnused(
  sb: SupabaseClient,
  tenantId: string,
  before: readonly GeosoftTemplateFile[],
): Promise<void> {
  const after = await loadGeosoftTemplates(sb, tenantId).catch(() => null);
  if (!after) return;
  const used = new Set(after.map((r) => r.object_path));
  const unused = [...new Set(before.map((r) => r.object_path))].filter((o) => !used.has(o));
  if (unused.length > 0) await sb.storage.from(GEOSOFT_TEMPLATE_BUCKET).remove(unused);
}

/**
 * Upload a type's set: each file not stored yet, then the manifest replaced (`current`: the organisation's live rows
 * now). Returns the organisation's live rows afterwards.
 */
export async function uploadGeosoftTemplate(
  sb: SupabaseClient,
  tenantId: string,
  family: QcFamily,
  files: readonly HashedTemplateFile[],
  current: readonly GeosoftTemplateFile[],
  progress?: (done: number, total: number) => void,
): Promise<GeosoftTemplateFile[]> {
  const stored = new Set(current.map((r) => r.sha256));
  const toSend = [...new Map(files.filter((f) => !stored.has(f.sha256)).map((f) => [f.sha256, f])).values()];
  for (const [i, f] of toSend.entries()) {
    progress?.(i, toSend.length);
    const up = await sb.storage
      .from(GEOSOFT_TEMPLATE_BUCKET)
      .upload(geosoftTemplateObjectPath(tenantId, f.sha256), new Blob([f.bytes as Uint8Array<ArrayBuffer>]), {
        contentType: 'application/octet-stream',
        upsert: false,
      });
    if (up.error && !alreadyThere(up.error))
      throw new Error(`${f.rel_path} was not stored: ${words(up.error)}`);
  }
  progress?.(toSend.length, toSend.length);
  const r = await sb.rpc('geosoft_template_replace', {
    p_tenant: tenantId,
    p_family: family,
    p_files: files.map((f) => ({ rel_path: f.rel_path, size_bytes: f.bytes.byteLength, sha256: f.sha256 })),
  });
  if (r.error) throw new Error(`The template was not saved: ${words(r.error)}`);
  await deleteUnused(
    sb,
    tenantId,
    current.filter((c) => c.family === family),
  );
  return loadGeosoftTemplates(sb, tenantId);
}

/** Remove a type's set; laptops remove their copy at their next sync. Returns the organisation's live rows. */
export async function removeGeosoftTemplate(
  sb: SupabaseClient,
  tenantId: string,
  family: QcFamily,
  current: readonly GeosoftTemplateFile[],
): Promise<GeosoftTemplateFile[]> {
  const r = await sb.rpc('geosoft_template_remove', { p_tenant: tenantId, p_family: family });
  if (r.error) throw new Error(`The template was not removed: ${words(r.error)}`);
  await deleteUnused(
    sb,
    tenantId,
    current.filter((c) => c.family === family),
  );
  return loadGeosoftTemplates(sb, tenantId);
}
